import { getAirbrake } from "@simplybusiness/services";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import {
  AIRBRAKE_PROJECT_ID,
  AIRBRAKE_PROJECT_KEY,
} from "../../constants/constants";
import type { WithMetadata } from "../../pages/Page/types";
import { getPropsByName, mapToArray, transformAnswers } from "../../utils";
import { apiRequest } from "../../utils/apiRequest";
import { useLocale } from "../useLocale";
import { useSetRedirect } from "../useRedirect";
import { useMutationStatus } from "../useMutationStatus";
import { useSetApiError } from "../useApiError";
import { useQuestionnaireActions } from "../../features/questionnaire";
import { questionnaireReducerAtom } from "../../features/questionnaire/state/questionnaireReducerAtom";
import type { Answers, PartialSection } from "../../features/questionnaire";
import { useTokenQueue } from "../useTokenQueue";
import type { Tokens } from "../../store";

const airbrake = getAirbrake({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY,
});

type QuestionsResponse = {
  data: {
    layout: {
      components: {
        metadata: {
          name: string;
        };
        props: {
          config?: Record<string, string>;
          sections: PartialSection[];
        };
      }[];
    };
    configuration: {
      authenticityToken: string;
      journeyToken: string;
    };
  };
};

/**
 * Calls the Questions API with the provided answers
 * and updates visible components, answers, changed questions,
 * and shouldUpdate triggers based on the response.
 *
 * Features:
 * - Uses shared token queue for sequential execution across all token-using hooks
 * - Timestamp-aware merging: local answers modified after request started are preserved
 * - Reads fresh answers at execution time (not stale answers from when queued)
 */
export const useSendAnswers = () => {
  const { enqueue } = useTokenQueue();
  const setRedirectUrl = useSetRedirect();
  const setApiError = useSetApiError();
  const { merge } = useQuestionnaireActions();
  const { locale } = useLocale();
  const isSendingAnswers = useMutationStatus("sendAnswers");

  // Read sections and visibility without subscribing to state changes
  const getQuestionnaireState = useAtomCallback(
    useCallback(get => get(questionnaireReducerAtom), []),
  );

  /**
   * Send answers to the Questions API.
   *
   * The `answers` parameter is for API compatibility but is ignored -
   * we read fresh answers from state at execution time. This ensures
   * we always send the latest state, not stale data from when queued.
   *
   * @returns Promise resolving to the redirect URL from the response config, if any
   */
  const sendAnswers = useCallback(
    (_answers: Answers): Promise<string | undefined> => {
      const requestStartTime = Date.now();

      return enqueue(async (tokens: Tokens) => {
        try {
          // Read CURRENT state at execution time (not stale state from when queued)
          const currentState = getQuestionnaireState();
          const freshAnswers = currentState.answers;

          const transformedAnswers = transformAnswers(
            freshAnswers,
            currentState.sections,
            currentState.visibility,
            locale,
          );
          const answersArray = mapToArray(transformedAnswers);

          const response = (await apiRequest("?step_action=questions", {
            ...tokens,
            questions: answersArray,
          })) as QuestionsResponse;

          // Clear any previous API error on success
          setApiError(null);

          // Get questionnaire props including config (for redirect URL)
          const questionnaireProps = getPropsByName({
            name: "questionnaire",
            components: response.data.layout.components,
          }) as WithMetadata<{
            sections: PartialSection[];
            config?: Record<string, string>;
          }>;

          const { sections, config } = questionnaireProps;

          // Merge data to Questionnaire state with requestStartTime for timestamp-aware merging
          merge(sections, requestStartTime);

          // Get redirect URL from the questionnaire component's config, not from the first component
          const redirectUrl = config?.redirectUrl;
          setRedirectUrl(redirectUrl);

          return {
            newTokens: response.data.configuration,
            result: redirectUrl,
          };
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error(`Error updating questions: ${error.message}`);
          airbrake.notify(error);
          setApiError("Something went wrong. Please try again.");
          throw error;
        }
      });
    },
    [
      enqueue,
      getQuestionnaireState,
      locale,
      merge,
      setApiError,
      setRedirectUrl,
    ],
  );

  return {
    sendAnswers,
    isSuccess: false, // No longer tracked via mutation - could add if needed
    isPending: isSendingAnswers,
    isError: false, // No longer tracked via mutation - could add if needed
    error: null, // No longer tracked via mutation - could add if needed
  };
};
